import { useState } from "react";
import PropTypes from "prop-types";
import { RefreshCw, Heart, Wifi, Settings, Download, AlertCircle, Zap, Square } from "lucide-react";
import {
  ResetModal,
  ChangeConfigModal,
  GetConfigModal,
  StartChargingModal,
  StopChargingModal,
  useChargerCommandHandlers,
} from "./charger_commands";
import "./SendCommands.scss";

const commands = [
  {
    id: "reset",
    title: "Reset Charger",
    description: "Restart the charger to its initial state",
    icon: RefreshCw,
    color: "#64748b",
    bg: "#fef2f2",
    border: "#fecaca",
    warning: "Interrupts any ongoing charging session",
  },
  {
    id: "heartbeat",
    title: "Set Heartbeat Interval",
    description: "Configure heartbeat interval for communication",
    icon: Heart,
    color: "#64748b",
    bg: "#fff7ed",
    border: "#fed7aa",
    info: "Default: 3600s (1 hour)",
  },
  {
    id: "websocket",
    title: "Set WebSocket Ping Interval",
    description: "Configure WebSocket ping interval",
    icon: Wifi,
    color: "#64748b",
    bg: "#eff6ff",
    border: "#bfdbfe",
    info: "Default: 30 seconds",
  },
  {
    id: "change-config",
    title: "Change Configuration",
    description: "Modify charger configuration parameters",
    icon: Settings,
    color: "#64748b",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    info: "Update any configurable parameter",
  },
  {
    id: "get-config",
    title: "Get Configuration",
    description: "Retrieve current charger configuration",
    icon: Download,
    color: "#64748b",
    bg: "#ecfdf5",
    border: "#a7f3d0",
    info: "Download current settings",
  },
  {
    id: "start-charging",
    title: "Start Charging",
    description: "Initiate a charging session on this charger",
    icon: Zap,
    color: "#059669",
    bg: "#ecfdf5",
    border: "#a7f3d0",
    info: "Validates breaker capacity before starting",
  },
  {
    id: "stop-charging",
    title: "Stop Charging",
    description: "Stop the active charging session",
    icon: Square,
    color: "#dc2626",
    bg: "#fef2f2",
    border: "#fecaca",
    warning: "Stops power delivery to the vehicle",
  },
];

export function SendCommands({ serialNumber, siteId, sendCommand, ratedCurrent }) {
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isHeartbeatModalOpen, setIsHeartbeatModalOpen] = useState(false);
  const [isWebSocketModalOpen, setIsWebSocketModalOpen] = useState(false);
  const [isChangeConfigModalOpen, setIsChangeConfigModalOpen] = useState(false);
  const [isGetConfigModalOpen, setIsGetConfigModalOpen] = useState(false);
  const [isStartChargingModalOpen, setIsStartChargingModalOpen] = useState(false);
  const [isStopChargingModalOpen, setIsStopChargingModalOpen] = useState(false);

  const {
    handleReset,
    handleChangeConfig,
    handleGetConfig,
    validateStartCharging,
    handleStartCharging,
    handleStopCharging,
    startChargingValidation,
    resetStartChargingValidation,
  } = useChargerCommandHandlers(serialNumber, siteId?.toString(), sendCommand);

  const openModal = id => {
    if (id === "reset") setIsResetModalOpen(true);
    if (id === "heartbeat") setIsHeartbeatModalOpen(true);
    if (id === "websocket") setIsWebSocketModalOpen(true);
    if (id === "change-config") setIsChangeConfigModalOpen(true);
    if (id === "get-config") setIsGetConfigModalOpen(true);
    if (id === "start-charging") {
      resetStartChargingValidation();
      setIsStartChargingModalOpen(true);
      validateStartCharging();
    }
    if (id === "stop-charging") setIsStopChargingModalOpen(true);
  };

  return (
    <div>
      <div className="sc-grid">
        {commands.map(cmd => {
          const Icon = cmd.icon;
          return (
            <div
              key={cmd.id}
              className="sc-card"
              role="button"
              tabIndex={0}
              style={{ "--sc-color": cmd.color, "--sc-bg": cmd.bg, "--sc-border": cmd.border }}
              onClick={() => openModal(cmd.id)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") openModal(cmd.id);
              }}
            >
              <div className="sc-icon">
                <Icon size={20} color={cmd.color} />
              </div>

              <div className="sc-content">
                <div className="sc-title">{cmd.title}</div>
                <div className="sc-desc">{cmd.description}</div>
                {cmd.warning && (
                  <div className="sc-warning">
                    <AlertCircle size={11} />
                    <span>{cmd.warning}</span>
                  </div>
                )}
                {cmd.info && (
                  <div className="sc-info">
                    <AlertCircle size={11} />
                    <span>{cmd.info}</span>
                  </div>
                )}
              </div>

              <div className="sc-arrow">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modals */}
      <ResetModal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} onSubmit={handleReset} />
      <ChangeConfigModal
        isOpen={isHeartbeatModalOpen}
        onClose={() => setIsHeartbeatModalOpen(false)}
        onSubmit={handleChangeConfig}
        title="Set Heartbeat Interval"
        configKey="HeartbeatInterval"
        inputLabel="Heartbeat interval (seconds)"
        defaultValue="3600"
      />
      <ChangeConfigModal
        isOpen={isWebSocketModalOpen}
        onClose={() => setIsWebSocketModalOpen(false)}
        onSubmit={handleChangeConfig}
        title="Set WebSocket Ping Interval"
        configKey="WebSocketPingInterval"
        inputLabel="WebSocket ping interval (seconds)"
        defaultValue="30"
      />
      <ChangeConfigModal
        isOpen={isChangeConfigModalOpen}
        onClose={() => setIsChangeConfigModalOpen(false)}
        onSubmit={handleChangeConfig}
        title="Change Configuration"
        configKey=""
        inputLabel="Value"
        defaultValue=""
        isGenericConfig={true}
      />
      <GetConfigModal
        isOpen={isGetConfigModalOpen}
        onClose={() => setIsGetConfigModalOpen(false)}
        onSubmit={handleGetConfig}
      />
      <StartChargingModal
        isOpen={isStartChargingModalOpen}
        onClose={() => setIsStartChargingModalOpen(false)}
        onSubmit={handleStartCharging}
        validationState={startChargingValidation}
        ratedCurrent={ratedCurrent}
      />
      <StopChargingModal
        isOpen={isStopChargingModalOpen}
        onClose={() => setIsStopChargingModalOpen(false)}
        onSubmit={handleStopCharging}
      />
    </div>
  );
}

SendCommands.propTypes = {
  serialNumber: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sendCommand: PropTypes.func.isRequired,
  ratedCurrent: PropTypes.number,
};

export default SendCommands;
